import React, { useContext, useEffect, useRef, useState } from "react";
import DevelopOptionsPanel from "../shared/components/DevelopOptionsPanel";

import { useNavigate, useParams } from "react-router-dom";
import { paths } from "../../Routes";
import { developOptions } from "../../config/developOptions";
import { THEME_NAMES } from "../../config/themes/config";
import { AuthContext } from "../../contexts/AuthContext";
import { ConfigContext } from "../../contexts/ConfigContext";
import { ProcessContext } from "../../contexts/ProcessContext";
import { useLanguages } from "../../utils/hooks/useLanguages";
import { RegistryMapper } from "../../utils/mappers/RegistryMapper";
import {
  findParticipantOnboardingDetailsByOnboardingDetailsId,
  getParticipantIdByOnboardingDetailsId,
  getParticipantIdByRole,
  findParticipantOnboardingDetailsByBorrowerId,
  findParticipantOnboardingDetailsByRole,
  getOnboardingDetailsIdFromLocation,
  getParticipantRoleByQueryParam,
} from "../../utils/participants";
import CriticalErrorMessage from "../shared/components/CriticalErrorMessage";
import {
  ONBOARDING_FULFILLED_BY_TYPES,
  ONBOARDING_PARTICIPANT_ROLES,
} from "../shared/config";
import FlowLoadingWindowManager from "../shared/sharedKernel/FlowLoadingWindowManager";
import FluxCapacitor, {
  SUCCESS_PATH,
} from "../shared/sharedKernel/FluxCapacitor";
import NotAllowedView from "./NotAllowedView";
import { NotFoundView } from "./NotFoundView";
import {
  CustomTheme,
  setEnvironment,
  // CustomRegistry,
} from "@nemuru-eng/js-api-client";
import { ENV_ENV } from "../../config/envVars";
import { useActionCallback } from "../../utils/hooks/useActionCallback";
import { TestButton } from "../shared/components/TestButton";
// import CallMeBackPanel from "../shared/components/CallMeBackPanel/CallMeBackPanel";

export function ApplicationView({ forceDevOptionsPanel, startPath }) {
  const {
    state: {
      loan,
      appStarted,
      merchant,
      income,
      optIns,
      documentationRequired,
      caixabank,
      processCriticalError,
      participant,
    },
    dispatch,
  } = useContext(ProcessContext);

  const {
    state: {
      loanOnAppStart,
      agent,
      lastActionsReference,
      lastActionsReferenceLastChange,
    },
    dispatch: authDispatch,
  } = useContext(AuthContext);

  const {
    state: {
      language,
      region,
      themeName,
      // registry },
    },
  } = useContext(ConfigContext);

  const { setLanguage } = useLanguages();

  const { loanId } = useParams();
  const navigate = useNavigate();

  const { dispatch: configDispatch } = useContext(ConfigContext);

  const defaultPath = startPath || `${paths.Application}/${loanId}`;

  const MAX_TIMEOUT = 45000;
  const RETRY_DELAY = 5000;

  const ALLOWED_ROLES = [
    ONBOARDING_PARTICIPANT_ROLES.BORROWER,
    ONBOARDING_PARTICIPANT_ROLES.CO_BORROWER,
    ONBOARDING_PARTICIPANT_ROLES.GUARANTOR,
  ];
  const ALLOWED_TYPES = [
    // "minimal",
    "borrower",
    "online",
  ];
  const ALLOWED_FULFILLED_BYS = [
    ONBOARDING_FULFILLED_BY_TYPES.BORROWER_POS,
    ONBOARDING_FULFILLED_BY_TYPES.CLIENT_SIDE_KYC_SIGNATURE,
    ONBOARDING_FULFILLED_BY_TYPES.MERCHANT_POS,
    ONBOARDING_FULFILLED_BY_TYPES.BORROWER_ONLINE,
  ];

  const [state, setState] = useState({
    startedAt: undefined,
    timer: undefined,
    notFound: undefined,
    notAllowed: undefined,
    wrongUrlParams: undefined,
  });

  const { startedAt, timer, notFound, notAllowed } = state;

  useEffect(() => {
    dispatch({
      type: "SET_FLOW_LOADING",
      payload: { show: true },
    });
    requestGetLoanAndAgent();
    setEnvironment(ENV_ENV);
  }, []);

  useEffect(() => {
    loanId && redirectToDefaultPathIfNotIn();
  }, [loanId]);

  useEffect(() => {
    if (loan) {
      setParticipantByUrlParam();
    }
  }, [loan]);

  useEffect(() => {
    if (themeName === CustomTheme.KTUIN) {
      getCheckoutSessionByPaymentMethodId();
      dispatch({
        type: "REQUEST_GET_LOAN_APPLICATIONS",
        payload: {
          loanId: loan.id,
        },
      });
    }
  }, [themeName]);

  const hasRequestedIdDocStatusRef = useRef(false);
  const onboardingDetailsId = participant?.onboardingDetails?.id;

  useEffect(() => {
    if (themeName !== CustomTheme.KTUIN) return;
    if (!onboardingDetailsId) return;
    if (hasRequestedIdDocStatusRef.current) return;

    hasRequestedIdDocStatusRef.current = true;
    dispatch({
      type: "REQUEST_GET_ID_DOCUMENT_VERIFICATION_STATUS",
      payload: { onboarding_details_id: onboardingDetailsId },
    });
  }, [onboardingDetailsId, themeName]);

  const isProcessAllowedByRoleTypeAndFulfilledBy = () => {
    const onBoardingDetailsFilteredByAllowedRoles =
      loanOnAppStart.onboarding_details.filter((onBoardingDetails) =>
        ALLOWED_ROLES.includes(onBoardingDetails.role)
      );
    const onBoardingDetailsFilteredByAllowedRolesAndTypes =
      onBoardingDetailsFilteredByAllowedRoles.filter((onBoardingDetails) =>
        ALLOWED_TYPES.includes(onBoardingDetails.type)
      );
    if (
      onBoardingDetailsFilteredByAllowedRolesAndTypes.filter(
        (onBoardingDetails) =>
          ALLOWED_FULFILLED_BYS.includes(onBoardingDetails.fulfilled_by)
      ).length > 0
    ) {
      return true;
    } else {
      return false;
    }
  };

  useEffect(() => {
    if (!loan && loanOnAppStart) {
      if (isProcessAllowedByRoleTypeAndFulfilledBy()) {
        setLoanOnProcessState();
      } else {
        redirectToNotAllowedView();
      }
    }
  }, [loanOnAppStart]);

  useEffect(() => {
    if (agent && isProcessAllowedByRoleTypeAndFulfilledBy()) {
      setLanguage();
      setRegistry();
      requestGetCaixabankKycProjection();
    }
  }, [agent]);

  useEffect(() => {
    if (caixabank.response && !caixabank.isNotNeeded) {
      retryGetCaixabankKycIfNeeded();
    }
  }, [caixabank]);

  useEffect(() => {
    if (loan && participant) {
      if (!allRequiredParamsOk) {
        doLoanRelatedRequestsIfNotDone();
        if (loan?.borrower_id) {
          requestGetOptInsIfNotDone();
        }
      } else {
        onAllNeededParamsAreOk();
      }
    }
  }, [participant]);

  useEffect(() => {
    if (
      !participant.onboardingDetails?.has_digital_footprint &&
      language &&
      region
    ) {
      postSetDigitalFootprint();
    }
  }, [language, participant]);

  useEffect(() => {
    if (loan?.borrower_id && language) {
      requestGetOptInsIfNotDone({ force: true });
    }
  }, [language]);

  useEffect(() => {
    const isActionTypeInLastActionsReference = (actionType) =>
      lastActionsReference?.some((element) => element.type === actionType);
    if (
      isActionTypeInLastActionsReference(
        "REQUEST_GET_LOAN_BEFORE_AGENT_SEQUENCE_REJECTED"
      ) ||
      isActionTypeInLastActionsReference("REQUEST_POST_SESSION_TOKEN_REJECTED")
    ) {
      redirectToNotFoundView();
    }
  }, [lastActionsReferenceLastChange]);

  const optInsAreNeeded = !optIns && participant.participantId;
  const caixabankCondition =
    caixabank.isNotNeeded || caixabank.isKnownCustomer !== undefined;

  const allRequiredParamsOk =
    income &&
    agent &&
    documentationRequired &&
    !optInsAreNeeded &&
    caixabankCondition &&
    participant?.onboardingDetails;

  const currentLoan = loan || loanOnAppStart;

  const postSetDigitalFootprint = () => {
    if (participant.onboardingDetails)
      dispatch({
        type: "REQUEST_POST_SET_DIGITAL_FOOTPRINT",
        payload: {
          id: participant.onboardingDetails.id,
          type: participant.onboardingDetails.type,
          locale: `${language}-${region}`,
        },
      });
  };

  useActionCallback("REQUEST_POST_SET_DIGITAL_FOOTPRINT_FULFILLED", () => {
    dispatch({
      type: "REQUEST_GET_LOAN",
      payload: loanId,
    });
  });

  const requestGetLoanAndAgent = () => {
    authDispatch({
      type: "REQUEST_GET_LOAN_BEFORE_AGENT_SEQUENCE",
      payload: loanId,
    });
  };

  const redirectToDefaultPathIfNotIn = () => {
    if (location.search) {
      const pathname = `${location.pathname}${location.search}`;
      navigate(pathname.replace(SUCCESS_PATH, ""));
    }

    if (
      location.pathname !== defaultPath &&
      !location.pathname.includes(paths.VideoId) &&
      !location.pathname.includes(paths.Contract) &&
      !location.search
    ) {
      navigate(defaultPath);
    }
  };

  const setLoanOnProcessState = () => {
    dispatch({
      type: "UPDATE_PROCESS_CONTEXT",
      payload: {
        loan: loanOnAppStart,
      },
    });
  };

  const getCheckoutSessionByPaymentMethodId = () => {
    dispatch({
      type: "REQUEST_GET_CHECKOUT_SESSION_BY_PAYMENT_METHOD_ID",
      payload: {
        paymentMethodId: loanOnAppStart.id,
      },
    });
  };

  const setRegistry = () => {
    configDispatch({
      type: "SET_REGISTRY",
      payload: {
        registry: RegistryMapper.hydrate(agent.custom_registry),
        fulfilledBy: participant.onboardingFulfilledBy,
      },
    });
  };

  const doLoanRelatedRequestsIfNotDone = () => {
    if (merchant === undefined) {
      requestGetMerchant();
    }
    if (income === undefined) {
      requestGetIncome();
    }
    if (documentationRequired === undefined) {
      requestGetDocumentationRequired();
    }
  };

  const requestGetMerchant = () => {
    dispatch({
      type: "REQUEST_GET_MERCHANT",
      payload: currentLoan.merchant_id,
    });
  };

  const requestGetIncome = () => {
    dispatch({
      type: "REQUEST_GET_DECLARED_INCOME",
      payload: {
        loanId: loan.id,
        onboardingDetailsId: participant.onboardingDetails.id,
      },
    });
  };

  const requestGetDocumentationRequired = () => {
    if (!participant.participantId) {
      dispatch({
        type: "UPDATE_PROCESS_CONTEXT",
        payload: {
          documentationRequired: {},
        },
      });
      return;
    }

    dispatch({
      type: "REQUEST_GET_DOCUMENTATION_REQUIRED",
      payload: {
        loanId: currentLoan.id,
        borrowerId: participant.participantId,
      },
    });
  };

  const requestGetCaixabankKycProjection = () => {
    if (caixabank.response) return;
    if (
      caixabank.response === undefined &&
      agent.custom_theme === THEME_NAMES.caixabank
    ) {
      dispatch({
        type: "REQUEST_GET_CAIXABANK_KYC_PROJECTION",
        payload: currentLoan.id,
      });
    } else {
      dispatch({
        type: "UPDATE_PROCESS_CONTEXT",
        payload: {
          caixabank: { ...caixabank, isNotNeeded: true },
        },
      });
    }
  };

  const retryGetCaixabankKycIfNeeded = () => {
    if (timer >= MAX_TIMEOUT) {
      dispatch({
        type: "UPDATE_PROCESS_CONTEXT",
        payload: { processCriticalError: true },
      });
      return;
    }
    if (
      caixabank.isKnownCustomer === undefined &&
      currentLoan.borrower_invitation.document_number
    ) {
      // Request getCaixabankKyc and update timer
      const retryDelay = startedAt ? RETRY_DELAY : 0;
      setTimeout(() => {
        dispatch({
          type: "REQUEST_GET_CAIXABANK_KYC",
          payload: {
            loanId: currentLoan.id,
            documentNumber: currentLoan.borrower_invitation.document_number,
          },
        });
        setState((s) => ({
          ...s,
          timer: Date.parse(new Date()) - startedAt,
        }));
      }, retryDelay);
    } else {
      dispatch({
        type: "UPDATE_PROCESS_CONTEXT",
        payload: {
          caixabank: { ...caixabank, isNotNeeded: true },
        },
      });
    }
    if (!startedAt) {
      setState((s) => ({
        ...s,
        startedAt: Date.parse(new Date()),
      }));
    }
  };

  const requestGetOptInsIfNotDone = ({ force } = { force: false }) => {
    if (force === true || optIns === undefined) {
      dispatch({
        type: "REQUEST_GET_OPTIN_BY_LOAN_ID_AND_BORROWER_ID",
        payload: {
          loanId: loan.id,
          borrowerId: participant.participantId,
          language: language,
        },
      });
    }
  };

  const onAllNeededParamsAreOk = () => {
    dispatch({
      type: "UPDATE_PROCESS_CONTEXT",
      payload: {
        flowNeedsCheck: true,
      },
    });
  };

  const redirectToNotFoundView = () => {
    setState((s) => ({
      ...s,
      notFound: true,
    }));
    // navigate(NotFoundViewPath);
    // }
  };

  const redirectToNotAllowedView = () => {
    setState((s) => ({
      ...s,
      notAllowed: true,
    }));
  };

  const getOnboardingDetailsFulfilledByQueryParams = (param) => {
    return Object.values(ONBOARDING_FULFILLED_BY_TYPES).find(
      (type) => type === param
    );
  };

  const setParticipantByUrlParam = async () => {
    let currentParticipantOnBoardingDetails;
    const queryParams = new URLSearchParams(location.search);

    const onboardingDetailsId = getOnboardingDetailsIdFromLocation();

    const onboardingFulfilledBy = queryParams.has("fb")
      ? getOnboardingDetailsFulfilledByQueryParams(queryParams.get("fb"))
      : undefined;

    const setParticipant = () => {
      dispatch({
        type: "SET_PARTICIPANT",
        payload: {
          role: currentParticipantOnBoardingDetails.role,
          participantId: borrowerId,
          onboardingDetails: currentParticipantOnBoardingDetails,
          onboardingFulfilledBy:
            onboardingFulfilledBy ??
            currentParticipantOnBoardingDetails.fulfilled_by,
        },
      });
    };

    if (location.pathname.includes(paths.VideoId)) {
      if (
        !queryParams.has("r") &&
        !queryParams.has("id") &&
        !queryParams.has("oid")
      ) {
        redirectToNotAllowedView();
        return;
      }
    }

    const participantRole = getParticipantRoleByQueryParam(loan);

    if (!participantRole) {
      return redirectToNotFoundView();
    }

    const borrowerId = onboardingDetailsId
      ? getParticipantIdByOnboardingDetailsId(loan, onboardingDetailsId)
      : getParticipantIdByRole(loan, participantRole);

    if (onboardingDetailsId) {
      currentParticipantOnBoardingDetails =
        findParticipantOnboardingDetailsByOnboardingDetailsId(
          loan,
          onboardingDetailsId
        );
      if (currentParticipantOnBoardingDetails) {
        setParticipant();
      }
    } else if (borrowerId) {
      currentParticipantOnBoardingDetails =
        findParticipantOnboardingDetailsByBorrowerId(loan, borrowerId);
      setParticipant();
    } else {
      currentParticipantOnBoardingDetails =
        findParticipantOnboardingDetailsByRole(loan, participantRole);
      setParticipant();
    }

    if (!currentParticipantOnBoardingDetails) {
      redirectToNotFoundView();
      return;
    }
  };

  return (
    <>
      {allRequiredParamsOk || appStarted ? (
        <div style={{ height: "100%" }}>
          {developOptions.SHOW_TEST_BUTTON && (
            <div style={{ position: "fixed", top: 14, left: 8 }}>
              <TestButton />
            </div>
          )}
          {(developOptions.SHOW_DEV_OPTIONS || forceDevOptionsPanel) && (
            <DevelopOptionsPanel />
          )}
          {/* {registry === CustomRegistry.FACILITEA && <CallMeBackPanel />} */}
          <FluxCapacitor loanId={loanId} path={defaultPath} />
        </div>
      ) : (
        <>
          {notFound ? (
            <NotFoundView />
          ) : notAllowed ? (
            <NotAllowedView />
          ) : (
            <FlowLoadingWindowManager />
          )}
          {processCriticalError && <CriticalErrorMessage />}
        </>
      )}
    </>
  );
}
